import { useEffect, useRef } from "react";
import { isBrowserDefined } from "../services/browserService";

const useEventListener = (
  eventName: string,
  handler: (event: Event) => void,
  element?: Document | HTMLElement,
) => {
  const savedHandler = useRef<(event: Event) => void>();

  // Default to document if browser is defined and no element is provided
  const targetElement = element || (isBrowserDefined() ? document : undefined);

  useEffect(() => {
    savedHandler.current = handler;
  }, [handler]);

  useEffect(() => {
    // Skip if no target element
    if (!targetElement) return;

    const isSupported = targetElement && "addEventListener" in targetElement;
    if (!isSupported) return;

    const eventListener = (event: Event) => savedHandler.current?.(event);

    targetElement.addEventListener(eventName, eventListener);

    return () => {
      targetElement.removeEventListener(eventName, eventListener);
    };
  }, [eventName, targetElement]);
};

export default useEventListener;
